import { useRef } from 'react';
import { Button, Card, CardContent, CardHeader } from '@billda/design-system';
import { UserSiteAssignments } from '../../UserSiteAssignments';
import { usePermissions } from '@billda/permissions-ui';

interface UserSiteAccessCardProps {
  userId: string;
  userName: string;
  isManaging: boolean;
  onManagingChange: (managing: boolean) => void;
}

export function UserSiteAccessCard({
  userId,
  userName,
  isManaging,
  onManagingChange,
}: UserSiteAccessCardProps) {
  const permissions = usePermissions();
  const canManage = permissions.includes('sites.assignments.manage');
  const manageButtonRef = useRef<HTMLButtonElement>(null);

  const handleManageChange = (managing: boolean) => {
    onManagingChange(managing);
    if (!managing) {
      requestAnimationFrame(() => manageButtonRef.current?.focus());
    }
  };

  return (
    <Card data-testid="user-site-access-card">
      <CardHeader>
        <h3 className="type-h6">Site Access</h3>
        {canManage && (
          <Button
            ref={manageButtonRef}
            variant="ghost"
            size="sm"
            onClick={() => handleManageChange(!isManaging)}
            data-testid="manage-sites-button"
          >
            {isManaging ? 'Done' : 'Manage'}
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-4">
        <div className="flex flex-col gap-4">
          <p className="type-body-md text-content-secondary">
            Controls which construction sites this user can access. Users can
            only view and manage data for sites they have been assigned to.
          </p>

          <UserSiteAssignments
            userId={userId}
            userName={userName}
            isManaging={isManaging}
            canManage={canManage}
          />
        </div>
      </CardContent>
    </Card>
  );
}
